import { createApi } from "@reduxjs/toolkit/query/react"
import { baseQueryWithReauth } from "@/app/utils.ts"
import {
  CallDataSourceDto,
  CreateCallDataSourceDto,
  ListResponse,
  UpdateCallDataSourceDto,
} from "@dialogics/shared/dto/types"
import {
  IngestionStatus,
  StartIngestionRequest,
  StartIngestionResponse,
  StartTranscriptionRequest,
  StartTranscriptionResponse,
  TranscriptionStatusResponse,
} from "@dialogics/shared/dto/temporal"

export const dataSourcesApi = createApi({
  reducerPath: "dataSourcesApi",
  baseQuery: baseQueryWithReauth({ baseUrl: "/api" }),
  tagTypes: ["CallDataSource", "Ingestion", "Transcription"],
  endpoints: builder => ({
    callDataSources: builder.query<ListResponse<CallDataSourceDto>, { organization_id: number }>({
      query: ({ organization_id }) => `/call-data-source?organization_id=${organization_id}`,
      providesTags: ["CallDataSource"],
    }),
    addCallDataSource: builder.mutation<CallDataSourceDto, CreateCallDataSourceDto>({
      query: data => ({
        url: "/call-data-source",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["CallDataSource"],
    }),
    updateCallDataSource: builder.mutation<CallDataSourceDto, { id: number } & UpdateCallDataSourceDto>({
      query: ({ id, ...data }) => ({
        url: `/call-data-source/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["CallDataSource"],
    }),
    deleteCallDataSource: builder.mutation<void, number>({
      query: id => ({
        url: `/call-data-source/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CallDataSource"],
    }),
    selectDebugSample: builder.mutation<{ count: number }, { id: number; count?: number }>({
      query: ({ id, count }) => ({
        url: `/call-data-source/${id}/sample`,
        method: "POST",
        body: { count },
      }),
    }),
    resetSampleFeatures: builder.mutation<{ deleted: number }, number>({
      query: id => ({
        url: `/call-data-source/${id}/sample/features`,
        method: "DELETE",
      }),
    }),
    ingestionStatus: builder.query<IngestionStatus | null, number>({
      query: id => `/call-data-source/${id}/ingestion`,
      providesTags: (_result, _error, id) => [{ type: "Ingestion", id }],
    }),
    startIngestion: builder.mutation<StartIngestionResponse, { id: number } & StartIngestionRequest>({
      query: ({ id, ...body }) => ({
        url: `/call-data-source/${id}/ingestion`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Ingestion", id }],
    }),
    transcriptionStatus: builder.query<TranscriptionStatusResponse, number>({
      query: id => `/call-data-source/${id}/transcription`,
      providesTags: (_result, _error, id) => [{ type: "Transcription", id }],
    }),
    startTranscription: builder.mutation<StartTranscriptionResponse, { id: number } & StartTranscriptionRequest>({
      query: ({ id, ...body }) => ({
        url: `/call-data-source/${id}/transcription`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Transcription", id }],
    }),
  }),
})

export const {
  useCallDataSourcesQuery,
  useAddCallDataSourceMutation,
  useUpdateCallDataSourceMutation,
  useDeleteCallDataSourceMutation,
  useSelectDebugSampleMutation,
  useResetSampleFeaturesMutation,
  useIngestionStatusQuery,
  useStartIngestionMutation,
  useTranscriptionStatusQuery,
  useStartTranscriptionMutation,
} = dataSourcesApi
